// Workspace roles and item sharing, shared by the server's permission checks and the web UI.
//
// Owner and admins manage the workspace; members see everything that isn't private.
// Guests only see items shared with them (Folder.shares, List.shares, Task.shares, Doc.shares)
// and everything inside those items, plus the channels they're in. They can't be added to
// spaces, create spaces, folders or channels, or invite people.
import type { ID, SharePermission, Workspace, WorkspaceRole } from './types.ts';

export type Role = 'owner' | 'member' | WorkspaceRole;

export function roleOf(ws: Pick<Workspace, 'ownerId' | 'roles'> | null | undefined, userId: ID | null | undefined): Role {
  if (!ws || !userId) return 'member';
  if (ws.ownerId === userId) return 'owner';
  return ws.roles?.[userId] ?? 'member';
}

/** Guests see only what's shared with them. */
export const isGuest = (role: Role | null | undefined) => role === 'guest';

/** Owner and admins change workspace settings and people. */
export const canManage = (role: Role) => role === 'owner' || role === 'admin';

export const SHARE_RANK: Record<SharePermission, number> = { view: 1, comment: 2, edit: 3, full: 4 };

export function atLeast(have: SharePermission | null | undefined, need: SharePermission): boolean {
  return !!have && SHARE_RANK[have] >= SHARE_RANK[need];
}

export function higher(a: SharePermission | null | undefined, b: SharePermission | null | undefined): SharePermission | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return SHARE_RANK[a] >= SHARE_RANK[b] ? a : b;
}

export const SHARE_PERMISSIONS: SharePermission[] = ['full', 'edit', 'comment', 'view'];
